// src/components/modules/TAI/TAIFilters.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Button,
  Space,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ESTADOS_ASIGNACION = [
  { value: "pendiente", label: "Pendiente", color: "#faad14" },
  { value: "asignado", label: "Asignado", color: "#1890ff" },
  { value: "confirmado", label: "Confirmado", color: "#52c41a" },
  { value: "ejecutado", label: "Ejecutado", color: "#13c2c2" },
  { value: "cancelado", label: "Cancelado", color: "#ff4d4f" },
];

const TIPOS_TRAYECTO = [
  { value: "interveredal", label: "Interveredal" },
  { value: "intermunicipal", label: "Intermunicipal" },
  { value: "urbano", label: "Urbano" },
];

const TAIFilters = ({ filters, onFilterChange, loading }) => {
  const [transportadoras, setTransportadoras] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Cargar datos para selectores
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // Cargar transportadoras
        const transResponse = await api.get("/transportadoras/activas");
        if (transResponse.data.success) {
          setTransportadoras(transResponse.data.data);
        }

        // Cargar zonas
        const zonasResponse = await api.get("/roles/zonas");
        if (zonasResponse.data) {
          setZonas(zonasResponse.data);
        }

        // Cargar EPS/empresas filtradas por rol del usuario
        const epsResponse = await api.get("/roles/clientes/usuario");
        if (epsResponse.data) {
          const { clientes, tiene_restriccion } = epsResponse.data;
          if (tiene_restriccion && clientes && clientes.length > 0) {
            // Mapear al formato esperado (id, nombre)
            setEmpresas(clientes.map(c => ({ id: c.id, nombre: c.nombre })));
          } else {
            // Sin restricción, cargar todos los clientes
            const allClientes = await api.get("/clientes/list");
            setEmpresas(allClientes.data.map(c => ({ id: c.id, nombre: c.nombre_cliente })));
          }
        }
      } catch (error) {
        console.error("Error cargando datos de filtros:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Manejar cambio de rango de fechas
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange({
        fechaInicio: dates[0].format("YYYY-MM-DD"),
        fechaFin: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      onFilterChange({
        fechaInicio: null,
        fechaFin: null,
      });
    }
  };

  // Limpiar todos los filtros
  const handleClearFilters = () => {
    onFilterChange({
      fechaInicio: dayjs().format("YYYY-MM-DD"),
      fechaFin: dayjs().add(7, "day").format("YYYY-MM-DD"),
      transportadora_id: null,
      estado_asignacion: null,
      trayecto_tipo: null,
      zona: null,
      empresa: null,
      status: [],
      searchQuery: "",
    });
  };

  return (
    <Card className="tai-filters-card" size="small">
      <Row gutter={[12, 12]} align="middle">
        {/* Rango de fechas */}
        <Col xs={24} sm={12} md={6}>
          <RangePicker
            value={
              filters.fechaInicio && filters.fechaFin
                ? [dayjs(filters.fechaInicio), dayjs(filters.fechaFin)]
                : null
            }
            onChange={handleDateRangeChange}
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            placeholder={["Fecha inicio", "Fecha fin"]}
            disabled={loading}
          />
        </Col>

        {/* Estado de asignación */}
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Estado asignación"
            value={filters.estado_asignacion}
            onChange={(value) => onFilterChange({ estado_asignacion: value })}
            allowClear
            style={{ width: "100%" }}
            disabled={loading}
          >
            {ESTADOS_ASIGNACION.map((estado) => (
              <Option key={estado.value} value={estado.value}>
                <span style={{ color: estado.color }}>{estado.label}</span>
              </Option>
            ))}
          </Select>
        </Col>

        {/* Tipo de trayecto */}
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Tipo trayecto"
            value={filters.trayecto_tipo}
            onChange={(value) => onFilterChange({ trayecto_tipo: value })}
            allowClear
            style={{ width: "100%" }}
            disabled={loading}
          >
            {TIPOS_TRAYECTO.map((tipo) => (
              <Option key={tipo.value} value={tipo.value}>
                {tipo.label}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Transportadora */}
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Transportadora"
            value={filters.transportadora_id}
            onChange={(value) => onFilterChange({ transportadora_id: value })}
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={loading || loadingData}
            loading={loadingData}
          >
            {transportadoras.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.nombre}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Zona */}
        <Col xs={24} sm={12} md={3}>
          <Select
            placeholder="Zona"
            value={filters.zona}
            onChange={(value) => onFilterChange({ zona: value })}
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={loading || loadingData}
          >
            {zonas.map((z) => (
              <Option key={z.id} value={z.nombre}>
                {z.nombre}
              </Option>
            ))}
          </Select>
        </Col>

        {/* EPS */}
        <Col xs={24} sm={12} md={3}>
          <Select
            placeholder="EPS"
            value={filters.empresa}
            onChange={(value) => onFilterChange({ empresa: value })}
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: "100%" }}
            disabled={loading || loadingData}
          >
            {empresas.map((e) => (
              <Option key={e.id} value={e.nombre}>
                {e.nombre}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[12, 12]} align="middle" style={{ marginTop: 12 }}>
        {/* Búsqueda */}
        <Col xs={24} sm={16} md={12}>
          <Input
            placeholder="Buscar por paciente, documento, autorización o tiquete..."
            prefix={<SearchOutlined />}
            value={filters.searchQuery}
            onChange={(e) => onFilterChange({ searchQuery: e.target.value })}
            allowClear
            disabled={loading}
          />
        </Col>

        {/* Botones */}
        <Col xs={24} sm={8} md={12} style={{ textAlign: "right" }}>
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              disabled={loading}
            >
              Limpiar
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

TAIFilters.propTypes = {
  filters: PropTypes.shape({
    fechaInicio: PropTypes.string,
    fechaFin: PropTypes.string,
    transportadora_id: PropTypes.number,
    estado_asignacion: PropTypes.string,
    trayecto_tipo: PropTypes.string,
    zona: PropTypes.string,
    empresa: PropTypes.string,
    status: PropTypes.array,
    searchQuery: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default TAIFilters;
